import {
  getLocalStorageValue,
  setLocalStorageValue,
} from "util/localStorageHelper";
import { styled } from "@mui/material/styles";
import { ReactNode, useCallback, useEffect, useRef } from "react";

import { IconButton, SlideProps } from "@mui/material";
import Slide from "@mui/material/Slide";

import CloseIcon from "@mui/icons-material/Close";
import { SnackbarKey, SnackbarProvider } from "notistack";
import { useIntl } from "react-intl";

import { useUserSetting } from "hooks/UserSettingsHooks";
import AuthService from "utils/authService";

const PREFIX = "SnackbarProviderWrapper";

const classes = {
  root: `${PREFIX}-root`,
  snackbarSuccess: `${PREFIX}-snackbarSuccess`,
  snackbarInfo: `${PREFIX}-snackbarInfo`,
  snackbarWarning: `${PREFIX}-snackbarWarning`,
  snackbarError: `${PREFIX}-snackbarError`,
};

const Root = styled("div")(({ theme }) => ({
  backgroundColor: "#FAF9F8",

  [`& .${classes.root}`]: {
    marginBottom: 20,
  },

  [`& .${classes.snackbarSuccess}`]: {
    backgroundColor: `${theme.palette.success.light} !important`,
    color: `${theme.palette.success.text} !important`,
    border: `1px solid ${theme.palette.success.dark}`,
  },

  [`& .${classes.snackbarInfo}`]: {
    backgroundColor: `${theme.palette.primary.light} !important`,
    color: `${theme.palette.primary.dark} !important`,
    border: `1px solid ${theme.palette.primary.dark}`,
  },

  [`& .${classes.snackbarWarning}`]: {
    backgroundColor: `${theme.palette.warning.light} !important`,
    color: `${theme.palette.warning.text} !important`,
    border: `1px solid ${theme.palette.warning.dark}`,
  },

  [`& .${classes.snackbarError}`]: {
    backgroundColor: `${theme.palette.error.light} !important`,
    color: `${theme.palette.error.text} !important`,
    border: `1px solid ${theme.palette.error.dark}`,
  },
}));

export function NotistackSnackbarProvider(props: { children: ReactNode }) {
  const intl = useIntl();

  const notistackRef = useRef<SnackbarProvider>(null);

  const [autoDismissSnackbars] = useUserSetting("autoDismissSnackbars", {
    query: {
      initialData: () =>
        getLocalStorageValue<boolean>("autoDismissSnackbars") ?? true,
      enabled: AuthService.isUserInStore(),
    },
  });

  useEffect(() => {
    setLocalStorageValue("autoDismissSnackbars", autoDismissSnackbars ?? true);
  }, [autoDismissSnackbars]);
  const autoHideDuration = (autoDismissSnackbars ?? true) ? 5000 : null;

  const closeAction = useCallback(
    (key: SnackbarKey) => (
      <IconButton
        key="close"
        aria-label={intl.formatMessage({
          defaultMessage: "Close",

          description: "Snackbar close button aria-label",
        })}
        color="inherit"
        onClick={() => notistackRef.current?.closeSnackbar(key)}
        size="large"
      >
        <CloseIcon />
      </IconButton>
    ),
    [intl, notistackRef.current],
  );

  const slideTransition = useCallback(
    (props: SlideProps) => <Slide {...props} direction="up" />,
    [],
  );

  return (
    <Root>
      <SnackbarProvider
        maxSnack={3}
        preventDuplicate
        autoHideDuration={autoHideDuration}
        ref={notistackRef}
        classes={{
          root: classes.root,
          variantSuccess: classes.snackbarSuccess,
          variantInfo: classes.snackbarInfo,
          variantWarning: classes.snackbarWarning,
          variantError: classes.snackbarError,
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        TransitionComponent={slideTransition}
        action={closeAction}
      >
        {props.children}
      </SnackbarProvider>
    </Root>
  );
}
